import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import Navbar from '../components/Navbar';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Heart, User, Target, Shield, Lightbulb, Share2, Award, FileText, Globe, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAbout();
  }, []);

  const loadAbout = async () => {
    try {
      const response = await api.getAboutInfo();
      setAbout(response.data);
    } catch (err) {
      console.error('Failed to load about info:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <main className="pt-20 pb-16 main-content">
        <div className="max-w-4xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-semibold text-slate-900 tracking-tight mb-2">
              About NoSpin-EBM
            </h1>
            <p className="text-sm text-slate-500 mb-4">(Evidence-Based Medicine)</p>
            <p className="text-lg text-slate-600">
              {about?.tagline || 'Data-Backed Medical Insights Without the Spin'}
            </p>
          </div>

          {/* Founder Section */}
          <Card className="mb-8 border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-sky-50 to-emerald-50 p-6 border-b border-slate-200">
              <div className="flex items-center gap-6">
                <img 
                  src="/images/founder-portrait.png" 
                  alt="Dr. Richard Lester - Founder"
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                />
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">
                    {about?.founder?.name || 'Dr. Richard Lester'}, MD, FRCPC
                  </h2>
                  <p className="text-sky-600 font-medium">Founder</p>
                  <p className="text-slate-600">
                    {about?.founder?.specialty || 'Infectious Disease Physician'}
                  </p>
                  <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                    <Globe className="h-3.5 w-3.5" />
                    {about?.founder?.location || 'Canada'}
                  </p>
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <p className="text-slate-700 leading-relaxed">
                {about?.founder?.mission || "To make grounded evidence and AI more accessible and explainable for clinicians, helping them make better decisions and provide better care to their patients."}
              </p>
            </CardContent>
          </Card>

          {/* About Text */}
          <Card className="mb-8 border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4 text-lg">Our Story</h3>
              <div className="prose prose-slate max-w-none">
                {about?.about_text?.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="text-slate-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Always Free Section */}
          <Card className="mb-8 border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-emerald-100">
                  <Heart className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg mb-2">
                    {about?.pricing_philosophy?.headline || 'Always Free • Pay What You Value'}
                  </h3>
                  <p className="text-slate-700 mb-4">
                    {about?.pricing_philosophy?.message || "NoSpin-EBM will always be free to use. If you find value in this tool, we welcome donations - but only what you can afford and feel it's worth to you."}
                  </p>
                  <div className="p-4 bg-white rounded-lg border border-emerald-200">
                    <h4 className="font-medium text-slate-900 mb-2">How Donations Help</h4>
                    <p className="text-sm text-slate-600">
                      {about?.pricing_philosophy?.donation_purpose || "Donations help cover server costs, keep the tool free for everyone, and allow us to continue improving NoSpin-EBM. Pay only for what you value, and only what you can afford."}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Features */}
          <div className="mb-8">
            <h3 className="font-semibold text-slate-900 text-lg mb-4 text-center">Key Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: Target, title: 'AI You Can Trust', desc: 'Evidence-based intelligence grounded in quality data' },
                { icon: Shield, title: 'GRADE Ratings', desc: 'Evidence quality ratings with PubMed links' },
                { icon: Globe, title: 'Local Context', desc: 'Regional guidelines and epidemiology' },
                { icon: Lightbulb, title: "Skeptic's Corner", desc: 'Identifies potential biases and spin' },
                { icon: Award, title: 'CME Credits', desc: 'Track and export for MOC (Canada)' },
                { icon: Share2, title: 'Easy Sharing', desc: 'Share results with colleagues' }
              ].map((feature, i) => (
                <Card key={i} className="border-slate-200">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-slate-100">
                      <feature.icon className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{feature.title}</h4>
                      <p className="text-sm text-slate-500">{feature.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Ambassadors Link */}
          <Card className="mb-8 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Award className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Our Ambassadors</h3>
                    <p className="text-sm text-slate-600">Meet the supporters who make NoSpin-EBM possible</p>
                  </div>
                </div>
                <Link to="/ambassadors">
                  <Button variant="outline" className="rounded-full" data-testid="view-ambassadors">
                    View All
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Version */}
          <p className="text-center text-sm text-slate-400">
            Version {about?.version || '1.0.0'}
          </p>
        </div>
      </main>
      <DisclaimerBanner />
    </div>
  );
};

export default AboutPage;
